/**
 * AttractionSelector — dropdown to pick an attraction from a list.
 * Supports single or multi-select mode, park filtering, and entity type icons.
 */
import React from 'react';
import { ChevronDown, Mountain, Film, UtensilsCrossed, ShoppingBag, Building2 } from 'lucide-react';
import type { Attraction } from '../hooks/useAttractions';

interface AttractionSelectorProps {
  value: string;
  attractions: Attraction[];
  onChange: (id: string) => void;
  isLoading: boolean;
  park?: string | null;
  multiMode?: boolean;
  multiValue?: string[];
  onChangeMulti?: (ids: string[]) => void;
}

const entityTypeStyles: Record<string, { icon: React.ReactNode; color: string }> = {
  ATTRACTION: { icon: <Mountain className="w-4 h-4" />, color: 'text-indigo-500 dark:text-indigo-400' },
  SHOW: { icon: <Film className="w-4 h-4" />, color: 'text-pink-500 dark:text-pink-400' },
  RESTAURANT: { icon: <UtensilsCrossed className="w-4 h-4" />, color: 'text-teal-500 dark:text-teal-400' },
  STORE: { icon: <ShoppingBag className="w-4 h-4" />, color: 'text-green-500 dark:text-green-400' },
  OTHER: { icon: <Building2 className="w-4 h-4" />, color: 'text-gray-400 dark:text-gray-500' },
};

function getEntityTypeStyles(entityType: string) {
  return entityTypeStyles[entityType] ?? entityTypeStyles.OTHER;
}


export function AttractionSelector({
  value,
  attractions,
  onChange,
  isLoading,
  park,
  multiMode = false,
  multiValue = [],
  onChangeMulti,
}: AttractionSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const [filterQuery, setFilterQuery] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);
  const filterInputRef = React.useRef<HTMLInputElement>(null);

  const selected = attractions.find((a) => a.id === value);
  const filtered = park
    ? attractions.filter((a) => a.parkName === park)
    : attractions;
  const displayList = filtered.filter((a) =>
    a.name.toLowerCase().includes(filterQuery.toLowerCase()),
  );

  React.useEffect(() => {
    if (open) filterInputRef.current?.focus();
    if (!open) setFilterQuery('');
  }, [open]);

  React.useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const base =
    'inline-flex items-center gap-2 px-3 py-2 text-sm rounded-full border transition-colors min-w-[240px]';

  const stateClass = open
    ? 'bg-disney-blue-500 text-white border-disney-blue-500'
    : (value || multiValue.length > 0)
      ? 'bg-white dark:bg-surface-dark-card text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-gray-400'
      : 'bg-white dark:bg-surface-dark-card text-gray-400 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-gray-400';

  const displayText = multiMode
    ? multiValue.length > 0
      ? `${multiValue.length} selected`
      : 'Select attractions...'
    : selected
      ? `${selected.name} (${selected.parkName})`
      : 'Select an attraction...';

  const isSelected = multiMode
    ? (id: string) => multiValue.includes(id)
    : (id: string) => id === value;

  const handleSelect = (attraction: Attraction) => {
    if (multiMode && onChangeMulti) {
      if (multiValue.includes(attraction.id)) {
        onChangeMulti(multiValue.filter((id) => id !== attraction.id));
      } else if (multiValue.length < 4) {
        onChangeMulti([...multiValue, attraction.id]);
      }
    } else {
      onChange(attraction.id);
    }
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={isLoading}
        className={`${base} ${stateClass}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {isLoading ? (
          <span>Loading attractions...</span>
        ) : multiMode ? (
          <span>{displayText}</span>
        ) : selected ? (
          <>
            <span className={getEntityTypeStyles(selected.entityType).color}>
              {getEntityTypeStyles(selected.entityType).icon}
            </span>
            <span className="font-medium">{selected.name}</span>
            <span className="text-xs opacity-60">({selected.parkName})</span>
          </>
        ) : (
          <span>Select an attraction...</span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1 bg-white dark:bg-surface-dark-card border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1 min-w-[280px] max-h-60 overflow-y-auto"
          role="listbox"
        >
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">Loading...</div>
          ) : (
            <>
              <div className="px-2 py-1.5">
                <input
                  ref={filterInputRef}
                  type="search"
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  placeholder="Filter attractions..."
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-surface-dark-card text-gray-700 dark:text-gray-300 focus:outline-none focus:border-disney-blue-500"
                />
              </div>
              {displayList.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">No attractions found</div>
              ) : (
                displayList.map((attraction) => {
                  const styles = getEntityTypeStyles(attraction.entityType);
                  const isItemSelected = isSelected(attraction.id);
                  const disabled = multiMode && !isItemSelected && multiValue.length >= 4;
                  return (
                    <button
                      key={attraction.id}
                      type="button"
                      onClick={() => handleSelect(attraction)}
                      disabled={disabled}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                        isItemSelected
                          ? 'bg-disney-blue-500 text-white'
                          : disabled
                            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                      role="option"
                      aria-selected={isItemSelected}
                    >
                      <span className={styles.color}>{styles.icon}</span>
                      <span className="font-medium">{attraction.name}</span>
                      <span className={`text-xs ml-auto ${isItemSelected ? 'text-white/60' : 'text-gray-400 dark:text-gray-500'}`}>
                        {attraction.parkName}
                      </span>
                      {isItemSelected && (
                        <svg className="w-4 h-4 ml-auto" viewBox="0 0 16 16" fill="none">
                          <path d="M3 8l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                  );
                })
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
